'use client'

import { useState, useRef, useEffect } from 'react'
import { Bell, Search, Share2, Bookmark, X, Settings, Check, Link as LinkIcon, Mail, Copy, Twitter, Facebook, Menu } from 'lucide-react'
import Link from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { useNotifications } from '@/contexts/NotificationsContext'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { InstallPWA } from '@/components/InstallPWA'

// Define props type for Header
interface HeaderProps {
  onToggleSidebar: () => void;
  title?: string;
  showHeader: boolean;
}

export default function Header({ onToggleSidebar, title, showHeader }: HeaderProps) {
  const { user, userAccess } = useAuth()
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const [isSavedActive, setIsSavedActive] = useState(false)
  
  // Get user display name, prioritizing profile table
  const getUserDisplayName = () => {
    // Prioritize full_name from userAccess (which contains profile)
    if (userAccess?.full_name) {
      return userAccess.full_name;
    }
    
    // Fallback to user_metadata.full_name if available
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    
    // Further fallback to first part of email if needed
    if (user?.email) {
      const emailParts = user.email.split('@');
      return emailParts[0]; // Just use the username part
    }
    
    // Default if nothing else is available
    return 'User';
  }
  
  const displayName = getUserDisplayName()
  
  // Function to get time-based greeting
  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return 'Good morning';
    } else if (currentHour < 18) {
      return 'Good afternoon';
    } else {
      return 'Good evening';
    }
  };

  const [showNotifications, setShowNotifications] = useState(false)
  const notificationsRef = useRef<HTMLDivElement>(null)
  
  // Check if current path is exactly /media-clips
  const isMediaClipsPage = pathname === '/media-clips'

  // Effect to check savedOnly param ONLY when on the media clips page
  useEffect(() => {
    if (isMediaClipsPage) {
      const savedOnly = searchParams.get('savedOnly') === 'true'
      setIsSavedActive(savedOnly)
      console.log('[Header Effect] On media clips page. Saved Only:', savedOnly);
    } else {
      // Reset active state if not on media clips page
      setIsSavedActive(false);
      console.log('[Header Effect] Not on media clips page, ensuring isSavedActive is false.');
    }
  }, [searchParams, pathname, isMediaClipsPage]) // Depend on pathname and searchParams
  
  // Toggles the saved clips filter on the media clips page
  const toggleSavedFilter = () => {
    const currentParams = new URLSearchParams(searchParams.toString());
    const isCurrentlySaved = currentParams.get('savedOnly') === 'true';
    let newParams = new URLSearchParams(); // Start with fresh params for navigation

    if (isCurrentlySaved) {
      // If filter is active, REMOVE 'savedOnly' and set default category filters.
      // All other parameters that might have been present with 'savedOnly' are discarded.
      newParams.set('category', 'Charity,Commercial,Sponsorship');
    } else {
      // If filter is NOT active, ADD 'savedOnly' and REMOVE all other filters.
      // This ensures that when viewing saved items, no other filters interfere.
      newParams = new URLSearchParams(); // Re-initialize to ensure it's clean before setting savedOnly
      newParams.set('savedOnly', 'true');
    }
    
    // Always navigate to the media clips page with the updated params
    const queryString = newParams.toString();
    router.push(`/media-clips${queryString ? '?' + queryString : ''}`);
  };
  
  // Handle clicks outside panels to close them
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <header className={cn(
      "fixed top-0 z-20 bg-[#141724] w-full transition-transform duration-300 ease-in-out",
      showHeader ? "translate-y-0" : "-translate-y-full",
      "md:translate-y-0" // Always visible on md+
    )}>
      <div className="flex h-[60px] items-center justify-between border-b border-gray-800 px-4 sm:px-6 md:px-8">
        {/* Left side: Page Title */}
        <div className="flex items-center">
          {/* Show title provided by props */}
          {title && (
            <h1 className="text-xl font-semibold text-white">
              {title}
            </h1>
          )}
        </div>

        {/* Header Controls (Right side) - Ensure proper containment and spacing */}
        <div className="flex items-center space-x-3 sm:space-x-4">
          {/* PWA Install Button */}
          <InstallPWA />

          {/* Saved Clips button - Always visible */}
          <button
            className={`rounded-full p-2 ${isSavedActive ? 'text-[#8A6FE8] bg-purple-900/30' : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'}`}
            onClick={toggleSavedFilter}
            title={isSavedActive ? "Show all clips" : "Show saved clips only"}
          >
            <Bookmark className={`h-5 w-5 ${isSavedActive ? 'fill-current' : ''}`} />
          </button>
          
          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <button 
              className="rounded-full p-2 text-gray-400 hover:bg-gray-700/50 hover:text-white"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[#FF5C5C]"></span>
              )}
            </button>
            
            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 rounded-md border border-gray-700 bg-[#1f2333] py-1 shadow-lg">
                <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
                  <h3 className="text-sm font-medium text-gray-300">Notifications</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button 
                        onClick={markAllAsRead} 
                        className="text-xs text-[#8A6FE8] hover:underline"
                      >
                        <Check className="h-3 w-3 inline-block mr-1" />
                        Mark all as read
                      </button>
                    )}
                    <Link href="/settings?section=notifications" className="text-xs text-[#8A6FE8] hover:underline">
                      <Settings className="h-3 w-3 inline-block mr-1" />
                      Settings
                    </Link>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length > 0 ? (
                    <>
                      {notifications.map((notification) => (
                        <div 
                          key={notification.id} 
                          className={`border-b border-gray-700 px-4 py-3 ${!notification.read ? 'bg-[#8A6FE8]/10' : ''}`}
                          onClick={() => markAsRead(notification.id)}
                        >
                          <div className="flex items-start justify-between">
                            {/* Combine Title and Message */}                            
                            <div className="flex-1 mr-2">
                              <p className="text-sm font-medium text-gray-200">{notification.title}</p>
                              {notification.action_url ? (
                                <Link 
                                  href={notification.action_url} 
                                  onClick={(e) => e.stopPropagation()} // Prevent marking as read when clicking link
                                  className="mt-0.5 text-xs text-gray-400 hover:text-[#8A6FE8] hover:underline inline-block"
                                >
                                  {notification.message} Click to view.
                                </Link>
                              ) : (
                                <p className="mt-0.5 text-xs text-gray-400">{notification.message}</p>
                              )}
                            </div>
                            {/* Unread indicator */}                            
                            {!notification.read && <span className="h-2 w-2 rounded-full bg-[#8A6FE8] mt-1 flex-shrink-0"></span>}
                          </div>
                          {/* Timestamp and Mark as Read */}                          
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-xs text-gray-400">{notification.time}</span>
                            {!notification.read && (
                              <button 
                                className="text-xs text-[#8A6FE8] hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                              >
                                Mark as read
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-gray-500">No notifications yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Settings link to replace profile avatar */}
          <Link
            href="/settings"
            className="hidden md:inline-flex rounded-full p-2 text-gray-400 hover:bg-gray-700/50 hover:text-white"
            title="Settings"
          >
            <Settings className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </header>
  )
} 